/**
 * NodeInspectorPanel — Full-width bottom panel for inspecting selected DAG nodes.
 *
 * Replaces the cramped 380px sidebar inspector with an IDE-style bottom panel
 * that gives code, data tables, and schema diffs proper horizontal space.
 *
 * Layout:
 *   - Collapsed (no node): 36px hint strip
 *   - Expanded (node selected): Header + Tabs (Code | Data | Schema | Review)
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Code as CodeIcon,
  TableChart as DataIcon,
  Schema as SchemaIcon,
  Verified as ReviewIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as PassIcon,
  AutoFixHigh as FixIcon,
  WarningAmber as WarnIcon,
  CompareArrows as MatchIcon,
  TouchApp as SelectIcon,
  CloudUpload as StorageIcon,
  Output as OutputIcon,
  InfoOutlined as InfoIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  useWorkspaceSession,
  useDiscardOperation,
  useTablePreview,
} from '../../hooks/useWorkspaceSession';
import type { WorkspaceOperation, WorkspaceTable, StepDriftResult } from '../../services/workspaceApi';
import type { PipelineNode } from './DAGCanvas';
import type { AddNodeType } from './AddNodeFAB';
import { useQuery } from 'react-query';
import DataExplorerTab from './DataExplorerTab';
import { categorizeChecks } from './reviewSummaryUtils';
import { getMappings } from '../../services/mappingApi';
import { usesMappingEditorTab } from '../../utils/mappingRunStatus';
import { getNodeColor, getNodeIcon, getTypeLabel } from './nodeConstants';
import CreationModePanel from './CreationModePanel';
import InspectorCodeTab from './InspectorCodeTab';

// ── Types ────────────────────────────────────────────────────────────

interface NodeInspectorPanelProps {
  selectedNode: PipelineNode | null;
  selectedOperation: WorkspaceOperation | null;
  sessionId: string;
  batchId?: string;
  onClose: () => void;
  /** Whether a pipeline exists for this session (to show re-run hint after edits). */
  pipelineExists?: boolean;
  /** Cancel the pending placeholder node (creation mode). */
  onCancelPending?: () => void;
  /** Callback after a pending node is successfully created. */
  onPendingCreated?: () => void;
  /** The operation type of the pending node. */
  pendingNodeType?: AddNodeType;
  /** Whether the agent is currently busy. */
  isBusy?: boolean;
  /** Available workspace tables for input selection in creation mode. */
  workspaceTables?: WorkspaceTable[];
  /** Callback to send a scoped message to the agent about a specific column. */
  onAskAboutColumn?: (tableName: string, columnName: string) => void;
  /** Callback when the active tab changes (for auto-expand logic in parent). */
  onActiveTabChange?: (tab: string) => void;
  /** Callback to send a free-form instruction to the agent about the selected node. */
  onAskAgent?: (instruction: string) => void;
  /** Schema evolution warning for the selected node (broken/changed columns). */
  schemaWarning?: { severity: 'error' | 'warning' | 'info'; message: string; brokenColumns?: string[] };
  /** Workspace mode — in 'mapping' mode, the inspector shows mapping details instead of tabs. */
  workspaceMode?: 'dag' | 'mapping';
  /** Whether the inspector is in fullscreen mode. */
  isFullscreen?: boolean;
  /** Toggle fullscreen mode for the inspector. */
  onToggleFullscreen?: () => void;
  /** Drift classification for the selected step (from pipeline versioning). */
  driftResult?: StepDriftResult | null;
  /** Callback to re-execute a step and refresh drift detection. */
  onReplayStep?: (stepId: string) => Promise<void>;
  /** Whether the SSE stream is connected (controls polling frequency). */
  sseConnected?: boolean;
  /** Restored creation state from sessionStorage (survives page refresh). */
  restoredCreationState?: { creationCode: string; outputName: string; inputRefs: string[]; creationExploratory: boolean } | null;
  /** Callback to persist creation form changes to sessionStorage. */
  onCreationStateChange?: (state: { code?: string; outputName?: string; inputRefs?: string[]; exploratory?: boolean }) => void;
  /** Health issues for cell-level violation highlighting in the Data tab. */
  healthIssues?: Array<{ column?: string; message: string; severity: 'error' | 'warning' | 'info'; remediation?: string; rowIndices?: number[] }>;
  /** Target schema snapshot for full-data validation via the Validate button. */
  targetSchemaSnapshot?: Record<string, any>;
}

type InspectorTab = 'code' | 'data' | 'schema' | 'review' | 'mapping' | 'output' | 'status' | 'drift';

// ── Component ────────────────────────────────────────────────────────

const NodeInspectorPanel: React.FC<NodeInspectorPanelProps> = ({
  selectedNode,
  selectedOperation,
  sessionId,
  batchId,
  onClose,
  pipelineExists,
  onCancelPending,
  onPendingCreated,
  pendingNodeType,
  isBusy,
  workspaceTables = [],
  onAskAboutColumn,
  onActiveTabChange,
  onAskAgent,
  schemaWarning,
  workspaceMode = 'dag',
  isFullscreen,
  onToggleFullscreen,
  driftResult,
  onReplayStep,
  sseConnected,
  restoredCreationState,
  onCreationStateChange,
  healthIssues,
  targetSchemaSnapshot,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<InspectorTab>('code');
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  // ── Creation mode ───────────────────────────────────────────────
  const isCreationMode = selectedNode?.stepId === '__pending__' && !!pendingNodeType;

  // ── Agent input state (non-creation) ──────────────────────────
  const [agentInput, setAgentInput] = useState('');

  const discard = useDiscardOperation(sessionId);
  const { data: session } = useWorkspaceSession(sessionId, { sseConnected });
  const sessionMeta = session?.workspace_meta;
  const showMappingEditor = usesMappingEditorTab(sessionMeta);

  // Reset tabs when node changes — default to Data tab (most common intent)
  useEffect(() => {
    let tab: InspectorTab;
    if (selectedNode?.stepType === 'mapping_output') {
      tab = showMappingEditor ? 'mapping' : 'data';
    } else if (selectedNode?.stepType === 'destination') {
      tab = 'status';
    } else {
      tab = 'data';
    }
    setActiveTab(tab);
    onActiveTabChange?.(tab);
  }, [selectedNode?.stepId, selectedNode?.stepType, selectedOperation, showMappingEditor]);

  // Preview data for output table.
  // Virtual nodes (pipeline_output) have no outputRef; use their inputRefs[0]
  // which points to the last transform's output table.
  // Source group nodes: outputRef is the stepId (for edge wiring), so use the
  // first child table name from _sourceTableNames for data preview.
  // For pending target_table nodes, only show the Data tab when the
  // __mapped_*__ view actually exists in the workspace.  During active
  // mapping generation the view isn't created yet, so we return null
  // (shows "No table selected").  Once the view is materialized — either
  // via materialize-mapping or iterative map_to_schema — it appears in
  // workspaceTables and we can preview it.
  const _wsTableNames = useMemo(
    () => new Set((workspaceTables ?? []).map((t) => t.name)),
    [workspaceTables],
  );
  const previewTableName = selectedNode?.status === 'failed' ? null
    // mapping_output is a virtual fan-out node — its outputRef is the
    // sentinel '__mapping_output__' (no underlying DuckDB table). The
    // inspector only renders the Mapping tab for this stepType (see
    // availableTabs below), which reads from the batch-mappings API,
    // not from previewData. Skip the preview fetch so we don't hit
    // data-worker with a guaranteed-404 lookup for the sentinel.
    : selectedNode?.stepType === 'mapping_output' ? null
    // target_schema_container's outputRef is a synthetic sentinel
    // ('__target_schema__') — no underlying DuckDB view. Same handling as
    // mapping_output: skip the preview fetch and let the Mapping tab read
    // from the batch-mappings API instead.
    : selectedNode?.stepType === 'target_schema_container' ? null
    : (selectedNode?.stepType === 'target_table' && selectedNode?.status === 'pending'
        && !_wsTableNames.has(selectedNode?.outputRef ?? '')) ? null
    : selectedNode?.stepType === 'source_group'
      ? ((selectedNode as any)._sourceTableNames?.[0] ?? null)
    : selectedNode?.outputRef
    || (selectedNode?.stepType === 'pipeline_output' && selectedNode?.inputRefs?.[0])
    || null;
  const { data: previewData } = useTablePreview(
    sessionId,
    previewTableName,
  );

  // Preview data for input table(s) — support multiple for joins
  const inputTableNames = useMemo((): string[] => {
    if (selectedOperation?.input_refs?.length) {
      return selectedOperation.input_refs;
    }
    const sql = selectedOperation?.config?.sql;
    if (sql) {
      const tableRefs = sql.match(/(?:FROM|JOIN)\s+(\w+)/gi);
      if (tableRefs) {
        const tables = tableRefs
          .map((ref: string) => ref.replace(/^(?:FROM|JOIN)\s+/i, ''))
          .filter((t: string) => t !== selectedOperation?.output_ref);
        return Array.from(new Set(tables));
      }
    }
    return [];
  }, [selectedOperation]);

  // For backward compat: primary input table (first one)
  const inputTableName = inputTableNames[0] || null;

  // Fetch preview for first input table (used for column diff)
  const { data: inputPreviewData } = useTablePreview(sessionId, inputTableName);

  // Fetch preview for second input table (joins)
  const secondInputTable = inputTableNames.length > 1 ? inputTableNames[1] : null;
  const { data: secondInputPreviewData } = useTablePreview(sessionId, secondInputTable);

  // Column diff — merge all input columns when computing diff
  const columnDiff = useMemo(() => {
    if (!inputPreviewData || !previewData) return null;
    const inputCols = new Set(inputPreviewData.columns);
    // For joins, also include second input table's columns
    if (secondInputPreviewData) {
      secondInputPreviewData.columns.forEach((c) => inputCols.add(c));
    }
    const outputCols = new Set(previewData.columns);
    const added = previewData.columns.filter((c) => !inputCols.has(c));
    const allInputCols = [...inputPreviewData.columns, ...(secondInputPreviewData?.columns || [])];
    const removed = allInputCols.filter((c) => !outputCols.has(c));
    // Deduplicate removed (same column may appear in both input tables)
    const uniqueRemoved = Array.from(new Set(removed));
    if (added.length === 0 && uniqueRemoved.length === 0) return null;
    return { added, removed: uniqueRemoved };
  }, [inputPreviewData, secondInputPreviewData, previewData]);

  // ── Mapping columns (from mapping API for mapping_output nodes) ──
  // Must be before the early return to keep hook order stable.
  const isMappingNode = selectedNode?.stepType === 'mapping_output' || selectedNode?.stepType === 'schema_map';
  const isTargetTableNode = selectedNode?.stepType === 'target_table';
  const needsMappingData = showMappingEditor && (isMappingNode || isTargetTableNode);
  const { data: mappingResponse } = useQuery(
    ['batch-mappings', batchId],
    () => getMappings(batchId!),
    { enabled: !!batchId && needsMappingData, staleTime: 30_000, retry: 1 },
  );
  const mappingColumns: Array<{
    source_column: string;
    target_column: string;
    status: 'mapped' | 'review' | 'unmapped';
    transform?: string;
  }> = useMemo(() => {
    if (!needsMappingData || !mappingResponse?.mappings) return [];
    let filteredMappings = mappingResponse.mappings;
    // For target_table nodes, filter to only the selected table's mappings
    if (isTargetTableNode && selectedNode?.title) {
      filteredMappings = filteredMappings.filter((m: any) => m.target_table === selectedNode.title);
    }
    return filteredMappings.map((m: any) => ({
      source_column: `${m.source_file}.${m.source_field}`,
      target_column: `${m.target_table}.${m.target_field}`,
      status: m.confidence >= 0.7 ? 'mapped' as const : m.confidence >= 0.4 ? 'review' as const : 'unmapped' as const,
      transform: m.transformation || undefined,
    }));
  }, [needsMappingData, isTargetTableNode, selectedNode?.title, mappingResponse]);

  // Quality checks for the strip between header and tabs
  const review = selectedOperation?.review_result;
  const qualityChecks = useMemo(
    () => categorizeChecks(review?.checks, review?.corrections, review?.warnings),
    [review?.checks, review?.corrections, review?.warnings],
  );


  // ── Mapping mode: hide inspector — mapping canvas already shows all info ──
  if (workspaceMode === 'mapping') {
    return (
      <Box
        sx={{
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          cursor: 'default',
        }}
      >
        <SelectIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
          Use the mapping canvas above to inspect fields
        </Typography>
      </Box>
    );
  }

  if (!selectedNode) return null;

  // ── Creation mode render ────────────────────────────────────────────

  if (isCreationMode && pendingNodeType && onCancelPending && onPendingCreated) {
    return (
      <CreationModePanel
        sessionId={sessionId}
        pendingNodeType={pendingNodeType}
        selectedNode={selectedNode!}
        workspaceTables={workspaceTables}
        onCancelPending={onCancelPending}
        onPendingCreated={onPendingCreated}
        isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen}
        isBusy={isBusy}
        restoredCreationState={restoredCreationState}
        onCreationStateChange={onCreationStateChange}
      />
    );
  }

  // ── Data for render ────────────────────────────────────────────────

  const nodeColor = getNodeColor(selectedNode.stepType);
  const nodeIcon = getNodeIcon(selectedNode.stepType);
  const typeLabel = getTypeLabel(selectedNode.stepType);
  const hasCode = selectedOperation?.config?.sql || selectedOperation?.config?.code || selectedOperation?.config?.function_code;
  const codeLanguage = selectedOperation?.config?.sql ? 'sql' : 'python';
  const codeContent = selectedOperation?.config?.sql || selectedOperation?.config?.function_code || selectedOperation?.config?.code || '';

  // ── Tab availability ───────────────────────────────────────────────

  const isVirtualNode = ['pipeline_output', 'destination', 'target_schema_container'].includes(selectedNode.stepType);
  const availableTabs: { value: InspectorTab; label: string; icon: React.ReactElement }[] = [];

  if (selectedNode.stepType === 'pipeline_output') {
    availableTabs.push({ value: 'output', label: 'Output', icon: <OutputIcon sx={{ fontSize: 14 }} /> });
    availableTabs.push({ value: 'data', label: 'Data', icon: <DataIcon sx={{ fontSize: 14 }} /> });
  } else if (selectedNode.stepType === 'destination') {
    availableTabs.push({ value: 'status', label: 'Status', icon: <InfoIcon sx={{ fontSize: 14 }} /> });
  } else if (selectedNode.stepType === 'mapping_output') {
    if (showMappingEditor) {
      availableTabs.push({ value: 'mapping', label: 'Mapping', icon: <MatchIcon sx={{ fontSize: 14 }} /> });
    }
  } else if (selectedNode.stepType === 'target_schema_container') {
    // Container has no underlying DuckDB view — only the Mapping tab makes
    // sense here (reads from the batch-mappings API). Drilling into a
    // specific table's data is done by clicking a row inside the node.
    if (showMappingEditor) {
      availableTabs.push({ value: 'mapping', label: 'Mapping', icon: <MatchIcon sx={{ fontSize: 14 }} /> });
    }
  } else if (selectedNode.stepType === 'target_table') {
    // Target table nodes: show Code (DDL), Data, and Mapping tabs
    if (selectedNode.codeSnippet) {
      availableTabs.push({ value: 'code', label: 'Code', icon: <CodeIcon sx={{ fontSize: 14 }} /> });
    }
    availableTabs.push({ value: 'data', label: 'Data', icon: <DataIcon sx={{ fontSize: 14 }} /> });
    if (showMappingEditor) {
      availableTabs.push({ value: 'mapping', label: 'Mapping', icon: <MatchIcon sx={{ fontSize: 14 }} /> });
    }
  } else {
    // Standard transform/source nodes
    if (hasCode) {
      availableTabs.push({ value: 'code', label: 'Code', icon: <CodeIcon sx={{ fontSize: 14 }} /> });
    }
    availableTabs.push({ value: 'data', label: 'Data', icon: <DataIcon sx={{ fontSize: 14 }} /> });
    if (columnDiff || (inputPreviewData && previewData)) {
      availableTabs.push({ value: 'schema', label: 'Schema', icon: <SchemaIcon sx={{ fontSize: 14 }} /> });
    }
    if (review) {
      availableTabs.push({ value: 'review', label: 'Review', icon: <ReviewIcon sx={{ fontSize: 14 }} /> });
    }
  }

  // Add drift tab when drift result exists for this step
  if (driftResult && (driftResult.status === 'red' || driftResult.status === 'amber')) {
    availableTabs.push({ value: 'drift', label: 'Drift', icon: <WarnIcon sx={{ fontSize: 14 }} /> });
  }

  // Ensure active tab is available; auto-select drift tab for broken/stale nodes
  const resolvedTab = (() => {
    if (driftResult && (driftResult.status === 'red' || driftResult.status === 'amber')
        && availableTabs.some((t) => t.value === 'drift') && activeTab !== 'drift') {
      return 'drift';
    }
    return availableTabs.some((t) => t.value === activeTab) ? activeTab : availableTabs[0]?.value || 'data';
  })();

  const tabChangeHandler = (_e: React.SyntheticEvent, v: string) => {
    setActiveTab(v as InspectorTab);
    onActiveTabChange?.(v);
  };

  const fullscreenTabs = (
    <Tabs
      value={resolvedTab}
      onChange={tabChangeHandler}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: 36,
        '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 1, minWidth: 40 },
      }}
    >
      {availableTabs.map((tab) => (
        <Tab
          key={tab.value}
          value={tab.value}
          icon={tab.icon}
          iconPosition="start"
          label={tab.label}
          sx={{ fontSize: 11, textTransform: 'none' }}
        />
      ))}
    </Tabs>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {isFullscreen ? (
        <>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 40,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tooltip title="Back to workspace">
              <IconButton
                size="small"
                onClick={() => onToggleFullscreen?.()}
                aria-label="Back to workspace"
                sx={{ flexShrink: 0 }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Typography variant="subtitle2" noWrap fontWeight={600} sx={{ fontSize: 12, maxWidth: 200, flexShrink: 0 }}>
              {selectedNode.title || selectedNode.stepType.replace(/_/g, ' ')}
            </Typography>
            <Chip
              label={typeLabel}
              size="small"
              sx={{
                fontSize: 10,
                height: 20,
                fontWeight: 600,
                flexShrink: 0,
                bgcolor: alpha(nodeColor, 0.1),
                color: nodeColor,
                border: 'none',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>{fullscreenTabs}</Box>
            {onToggleFullscreen && (
              <Tooltip title="Exit fullscreen">
                <IconButton size="small" onClick={() => onToggleFullscreen()} sx={{ flexShrink: 0 }}>
                  <FullscreenExitIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Close inspector">
              <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {(onAskAgent || (selectedOperation && selectedOperation.status !== 'discarded')) && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              {onAskAgent && (
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Ask about this step..."
                  value={agentInput}
                  onChange={(e) => setAgentInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && agentInput.trim()) {
                      onAskAgent(agentInput.trim());
                      setAgentInput('');
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        disabled={!agentInput.trim()}
                        onClick={() => {
                          if (agentInput.trim()) {
                            onAskAgent(agentInput.trim());
                            setAgentInput('');
                          }
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <SendIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    ),
                    sx: { fontSize: 11, height: 28, pr: 0.5 },
                  }}
                  sx={{ flex: 1, minWidth: 0, maxWidth: 480 }}
                />
              )}
              {selectedOperation && selectedOperation.status !== 'discarded' && (
                <Tooltip title="Remove this step and drop its output table from the workspace">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
                      onClick={() => discard.mutate(selectedOperation.operation_id)}
                      disabled={discard.isLoading}
                      sx={{ fontSize: 10, textTransform: 'none', py: 0, px: 1.5, borderRadius: 1.5, minHeight: 26 }}
                    >
                      Delete
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
        </>
      ) : (
        <>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* Node icon */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 0.75,
            bgcolor: nodeColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {nodeIcon}
        </Box>

        {/* Title */}
        <Typography variant="subtitle2" noWrap fontWeight={600} sx={{ fontSize: 12, maxWidth: 200 }}>
          {selectedNode.title || selectedNode.stepType.replace(/_/g, ' ')}
        </Typography>

        {/* Type badge */}
        <Chip
          label={typeLabel}
          size="small"
          sx={{
            fontSize: 10,
            height: 20,
            fontWeight: 600,
            bgcolor: alpha(nodeColor, 0.1),
            color: nodeColor,
            border: 'none',
          }}
        />

        {/* Row delta */}
        {selectedOperation?.row_count_before != null && selectedOperation?.row_count_after != null && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" sx={{ fontSize: 11, color: 'text.disabled' }}>
              {selectedOperation.row_count_before.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 11 }}>→</Typography>
            <Typography variant="caption" fontWeight={500} sx={{ fontSize: 11 }}>
              {selectedOperation.row_count_after.toLocaleString()}
            </Typography>
            {(() => {
              const delta = selectedOperation.row_count_after! - selectedOperation.row_count_before!;
              if (delta === 0) return null;
              return (
                <Chip
                  label={`${delta > 0 ? '+' : ''}${delta.toLocaleString()}`}
                  size="small"
                  color={delta < 0 ? 'error' : 'success'}
                  variant="outlined"
                  sx={{ fontSize: 9, height: 16, '& .MuiChip-label': { px: 0.5 } }}
                />
              );
            })()}
          </Stack>
        )}

        {/* Metadata (for source/virtual nodes without operations) */}
        {(['source', 'source_group', 'pipeline_output', 'destination'].includes(selectedNode.stepType)) && selectedNode.rowCount != null && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ fontSize: 11 }}>
              {selectedNode.rowCount.toLocaleString()} rows
            </Typography>
            {selectedNode.columnCount != null && (
              <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
                · {selectedNode.columnCount} cols
              </Typography>
            )}
          </Stack>
        )}

        {/* Quality check chips (inline) */}
        {qualityChecks.length > 0 ? (
          qualityChecks.map((ch) => (
            <Chip
              key={ch.name}
              size="small"
              variant="outlined"
              icon={
                ch.status === 'pass' ? <PassIcon sx={{ fontSize: 11 }} /> :
                ch.status === 'fixed' ? <FixIcon sx={{ fontSize: 11 }} /> :
                <WarnIcon sx={{ fontSize: 11 }} />
              }
              label={ch.displayLabel}
              color={
                ch.status === 'pass' ? 'success' :
                ch.status === 'fixed' ? 'info' :
                'warning'
              }
              sx={{ fontSize: 10, height: 20, '& .MuiChip-icon': { fontSize: 11 } }}
            />
          ))
        ) : selectedNode.reviewResult ? (
          <Chip
            size="small"
            variant="outlined"
            icon={
              selectedNode.reviewResult === 'pass' ? <PassIcon sx={{ fontSize: 12 }} /> :
              selectedNode.reviewResult === 'fixed' ? <FixIcon sx={{ fontSize: 12 }} /> :
              <WarnIcon sx={{ fontSize: 12 }} />
            }
            label={selectedNode.reviewResult === 'pass' ? 'Pass' : selectedNode.reviewResult === 'fixed' ? 'Fixed' : 'Warning'}
            color={selectedNode.reviewResult === 'pass' ? 'success' : selectedNode.reviewResult === 'fixed' ? 'info' : 'warning'}
            sx={{ fontSize: 10, height: 20 }}
          />
        ) : null}

        <Box sx={{ flex: 1 }} />

        {/* Ask Agent */}
        {onAskAgent && (
          <TextField
            size="small"
            variant="outlined"
            placeholder="Ask about this step..."
            value={agentInput}
            onChange={(e) => setAgentInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && agentInput.trim()) {
                onAskAgent(agentInput.trim());
                setAgentInput('');
              }
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  disabled={!agentInput.trim()}
                  onClick={() => {
                    if (agentInput.trim()) {
                      onAskAgent(agentInput.trim());
                      setAgentInput('');
                    }
                  }}
                  sx={{ p: 0.25 }}
                >
                  <SendIcon sx={{ fontSize: 14 }} />
                </IconButton>
              ),
              sx: { fontSize: 11, height: 28, pr: 0.5 },
            }}
            sx={{ flex: 1, maxWidth: 280, mr: 0.5 }}
          />
        )}

        {/* Delete — any ledger-backed transform (sql, python, fuzzy, llm, etc.), any status except already discarded */}
        {selectedOperation && selectedOperation.status !== 'discarded' && (
          <Tooltip title="Remove this step and drop its output table from the workspace">
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
                onClick={() => discard.mutate(selectedOperation.operation_id)}
                disabled={discard.isLoading}
                sx={{ fontSize: 10, textTransform: 'none', py: 0, px: 1.5, borderRadius: 1.5, minHeight: 26 }}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Fullscreen toggle */}
        {onToggleFullscreen && (
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={onToggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 16 }} /> : <FullscreenIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}

        {/* Close */}
        <IconButton size="small" onClick={onClose} sx={{ ml: 0.5 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={resolvedTab}
          onChange={tabChangeHandler}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 1, minWidth: 40 },
          }}
        >
          {availableTabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              sx={{ fontSize: 11, textTransform: 'none' }}
            />
          ))}
        </Tabs>
      </Box>
        </>
      )}

      {/* ── Tab content ────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Code Tab */}
        {resolvedTab === 'code' && (
          <InspectorCodeTab
            selectedOperation={selectedOperation}
            selectedNode={selectedNode}
            sessionId={sessionId}
            codeLanguage={codeLanguage}
            codeContent={codeContent}
            hasCode={!!hasCode}
            pipelineExists={pipelineExists}
            schemaWarning={schemaWarning}
            onAskAgent={onAskAgent}
            workspaceTables={workspaceTables}
            onCodeSaved={() => setDataRefreshKey((k) => k + 1)}
          />
        )}

        {/* Data Tab — Interactive Explorer */}
        {resolvedTab === 'data' && (
          <DataExplorerTab
            sessionId={sessionId}
            tableName={previewTableName}
            inputTableName={inputTableName}
            columnDiff={columnDiff}
            isSourceNode={selectedNode?.stepType === 'source' || selectedNode?.stepType === 'source_group'}
            operationContext={selectedOperation ? {
              operationId: selectedOperation.operation_id,
              operationType: selectedOperation.operation_type,
              config: selectedOperation.config,
            } : null}
            workspaceTables={workspaceTables}
            dataRefreshKey={dataRefreshKey}
            onAskAboutColumn={onAskAboutColumn}
            errorMessage={selectedNode?.status === 'failed' && selectedNode?.description?.startsWith('Error: ') ? selectedNode.description.slice(7) : null}
            healthIssues={selectedNode?.stepType === 'target_table' ? healthIssues : undefined}
            targetSchemaSnapshot={selectedNode?.stepType === 'target_table'
              ? (targetSchemaSnapshot
                ?? mappingResponse?.metadata?.target_schema_snapshot
                ?? (mappingResponse as any)?.transformation_plan?.target_schema_snapshot)
              : undefined}
          />
        )}

        {/* Mapping Tab (for mapping_output nodes) */}
        {resolvedTab === 'mapping' && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            {mappingColumns.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography variant="body2" color="text.disabled">
                  No mapping data available yet
                </Typography>
              </Box>
            ) : (
              <>
                {/* Summary strip */}
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: alpha('#059669', 0.03) }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                      {mappingColumns.filter((c) => c.status === 'mapped').length}/{mappingColumns.length} columns mapped
                    </Typography>
                    {mappingColumns.some((c) => c.status === 'review') && (
                      <Chip
                        label={`${mappingColumns.filter((c) => c.status === 'review').length} need review`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                    )}
                    {mappingColumns.some((c) => c.status === 'unmapped') && (
                      <Chip
                        label={`${mappingColumns.filter((c) => c.status === 'unmapped').length} unmapped`}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                    )}
                  </Stack>
                </Box>

                {/* Mapping table */}
                <TableContainer sx={{ maxHeight: 'calc(100% - 40px)' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', py: 0.75, width: 40 }}>
                          #
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', py: 0.75 }}>
                          Source Column
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', py: 0.75, width: 30, textAlign: 'center' }}>
                          →
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', py: 0.75 }}>
                          Target Column
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', py: 0.75, width: 80 }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', py: 0.75 }}>
                          Transform
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mappingColumns.map((col, i) => (
                        <TableRow
                          key={i}
                          hover
                          sx={{
                            bgcolor:
                              col.status === 'review'
                                ? alpha('#f59e0b', 0.04)
                                : col.status === 'unmapped'
                                  ? alpha('#ef4444', 0.04)
                                  : 'transparent',
                          }}
                        >
                          <TableCell sx={{ fontSize: 10, color: 'text.disabled', py: 0.5 }}>
                            {i + 1}
                          </TableCell>
                          <TableCell sx={{ fontFamily: '"Fira Code", monospace', fontSize: 11, py: 0.5 }}>
                            {col.source_column || '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>→</Typography>
                          </TableCell>
                          <TableCell sx={{ fontFamily: '"Fira Code", monospace', fontSize: 11, py: 0.5, fontWeight: 500 }}>
                            {col.target_column}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            {col.status === 'mapped' && (
                              <Chip
                                icon={<PassIcon sx={{ fontSize: 10 }} />}
                                label="Mapped"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: 9, height: 18, '& .MuiChip-icon': { ml: 0.25 } }}
                              />
                            )}
                            {col.status === 'review' && (
                              <Chip
                                icon={<WarnIcon sx={{ fontSize: 10 }} />}
                                label="Review"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: 9, height: 18, '& .MuiChip-icon': { ml: 0.25 } }}
                              />
                            )}
                            {col.status === 'unmapped' && (
                              <Chip
                                label="Unmapped"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: 9, height: 18 }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 10, color: 'text.secondary', py: 0.5 }}>
                            {col.transform || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}

        {/* Schema Tab */}
        {resolvedTab === 'schema' && (inputPreviewData || columnDiff) && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            <Stack direction="row" spacing={4}>
              {/* Input schema(s) — show each input table separately for joins */}
              {[
                { name: inputTableName, data: inputPreviewData },
                ...(secondInputPreviewData ? [{ name: secondInputTable, data: secondInputPreviewData }] : []),
              ].map(({ name, data }) => data && (
                <Box key={name} sx={{ flex: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                    Input Schema ({name || 'source'})
                  </Typography>
                  {data.columns.map((col) => {
                    const isRemoved = columnDiff?.removed.includes(col) ?? false;
                    return (
                      <Box
                        key={col}
                        sx={{
                          py: 0.5,
                          px: 1,
                          mb: 0.25,
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: isRemoved ? alpha('#ef4444', 0.06) : 'transparent',
                          borderLeft: isRemoved ? '3px solid #ef4444' : '3px solid transparent',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Fira Code", monospace',
                            fontSize: 12,
                            color: isRemoved ? '#991b1b' : 'text.primary',
                          }}
                        >
                          {col}
                        </Typography>
                        {isRemoved && (
                          <Chip label="REMOVED" size="small" color="error" variant="outlined" sx={{ fontSize: 9, height: 16 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}

              {/* Divider */}
              <Divider orientation="vertical" flexItem />

              {/* Output schema */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Output Schema ({previewTableName || 'output'})
                </Typography>
                {previewData?.columns.map((col) => {
                  const isAdded = columnDiff?.added.includes(col) ?? false;
                  return (
                    <Box
                      key={col}
                      sx={{
                        py: 0.5,
                        px: 1,
                        mb: 0.25,
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: isAdded ? alpha('#22c55e', 0.06) : 'transparent',
                        borderLeft: isAdded ? '3px solid #22c55e' : '3px solid transparent',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Fira Code", monospace',
                          fontSize: 12,
                          color: isAdded ? '#166534' : 'text.primary',
                        }}
                      >
                        {col}
                      </Typography>
                      {isAdded && (
                        <Chip label="NEW" size="small" color="success" variant="outlined" sx={{ fontSize: 9, height: 16 }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          </Box>
        )}

        {/* Output Tab (for pipeline_output virtual node) */}
        {resolvedTab === 'output' && selectedNode.stepType === 'pipeline_output' && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2.5 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#2563eb', 0.04),
                  border: '1px solid',
                  borderColor: alpha('#2563eb', 0.15),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <PassIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 12, color: '#2563eb' }}>
                    Pipeline Output Ready
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  {selectedNode.rowCount != null && (
                    <Box>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Rows</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                        {selectedNode.rowCount.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {selectedNode.columnCount != null && (
                    <Box>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Columns</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                        {selectedNode.columnCount}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Use the <b>Download CSV</b> button on the node to download the cleaned data,
                or explore the data in the <b>Data</b> tab.
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Status Tab (for destination virtual node) */}
        {resolvedTab === 'status' && selectedNode.stepType === 'destination' && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2.5 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#7c3aed', 0.04),
                  border: '1px solid',
                  borderColor: alpha('#7c3aed', 0.15),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <StorageIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 12, color: '#7c3aed' }}>
                    Destination Export
                  </Typography>
                  <Chip
                    label={selectedNode._destinationStatus || selectedNode.status}
                    size="small"
                    color={
                      selectedNode.status === 'completed' ? 'success'
                        : selectedNode.status === 'running' ? 'info'
                        : selectedNode.status === 'failed' ? 'error'
                        : 'default'
                    }
                    sx={{ fontSize: 10, height: 20, textTransform: 'capitalize' }}
                  />
                </Stack>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {selectedNode._connectorType && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, width: 70 }}>Connector</Typography>
                      <Typography variant="body2" sx={{ fontSize: 12 }}>{selectedNode._connectorType}</Typography>
                    </Stack>
                  )}
                  {selectedNode._targetTable && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, width: 70 }}>Target</Typography>
                      <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace' }}>{selectedNode._targetTable}</Typography>
                    </Stack>
                  )}
                  {selectedNode.rowCount != null && selectedNode.status === 'completed' && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, width: 70 }}>Rows</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>{selectedNode.rowCount.toLocaleString()}</Typography>
                    </Stack>
                  )}
                  {selectedNode._destinationError && (
                    <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: alpha('#ef4444', 0.06), border: '1px solid', borderColor: alpha('#ef4444', 0.15) }}>
                      <Typography variant="caption" color="error" sx={{ fontSize: 11 }}>
                        {selectedNode._destinationError}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Review Tab */}
        {resolvedTab === 'review' && review && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            {/* Overall status */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              {review.passed ? (
                <PassIcon sx={{ fontSize: 20, color: 'success.main' }} />
              ) : (
                <WarnIcon sx={{ fontSize: 20, color: 'warning.main' }} />
              )}
              <Typography variant="subtitle2" fontWeight={600}>
                {review.passed ? 'Quality checks passed' : 'Quality issues detected'}
              </Typography>
            </Stack>

            {/* Checks */}
            {review.checks && review.checks.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Checks ({review.checks.length})
                </Typography>
                {review.checks.map((check: any, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.5,
                      borderRadius: 0.5,
                      bgcolor: check.passed ? alpha('#22c55e', 0.04) : alpha('#ef4444', 0.04),
                      borderLeft: `3px solid ${check.passed ? '#22c55e' : '#ef4444'}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {check.passed ? (
                        <PassIcon sx={{ fontSize: 14, color: 'success.main' }} />
                      ) : (
                        <WarnIcon sx={{ fontSize: 14, color: 'error.main' }} />
                      )}
                      <Typography sx={{ fontSize: 12 }}>
                        {check.check_name || check.description || `Check ${i + 1}`}
                      </Typography>
                    </Stack>
                    {check.message && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25, pl: 3 }}>
                        {check.message}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Corrections */}
            {review.corrections && review.corrections.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Auto-Corrections ({review.corrections.length})
                </Typography>
                {review.corrections.map((corr: any, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.5,
                      borderRadius: 0.5,
                      bgcolor: alpha('#3b82f6', 0.04),
                      borderLeft: '3px solid #3b82f6',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FixIcon sx={{ fontSize: 14, color: 'info.main' }} />
                      <Typography sx={{ fontSize: 12 }}>
                        {corr.description || `Correction ${i + 1}`}
                      </Typography>
                      {corr.succeeded != null && (
                        <Chip
                          label={corr.succeeded ? 'Applied' : 'Failed'}
                          size="small"
                          color={corr.succeeded ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ fontSize: 9, height: 16 }}
                        />
                      )}
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}

            {/* Warnings */}
            {review.warnings && review.warnings.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Warnings ({review.warnings.length})
                </Typography>
                {review.warnings.map((warn: any, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.5,
                      borderRadius: 0.5,
                      bgcolor: alpha('#f59e0b', 0.06),
                      borderLeft: '3px solid #f59e0b',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarnIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography sx={{ fontSize: 12 }}>
                        {typeof warn === 'string' ? warn : warn.message || warn.description || `Warning ${i + 1}`}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Drift Tab ────────────────────────────────────────────── */}
        {resolvedTab === 'drift' && driftResult && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip
                label={driftResult.status.toUpperCase()}
                size="small"
                color={driftResult.status === 'red' ? 'error' : 'warning'}
                sx={{ fontWeight: 700, fontSize: 11 }}
              />
              <Typography variant="subtitle2" fontWeight={600}>
                Schema Drift Detected
              </Typography>
            </Stack>

            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
              {driftResult.reason}
            </Typography>

            {driftResult.column_drifts.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  Column Changes ({driftResult.column_drifts.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>Column</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>Change</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>Old Type</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>New Type</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 600, py: 0.5 }}>Impact</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {driftResult.column_drifts.map((cd, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontSize: 11, py: 0.5, fontFamily: 'monospace' }}>{cd.column}</TableCell>
                          <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                            <Chip
                              label={cd.drift_type}
                              size="small"
                              color={cd.drift_type === 'removed' ? 'error' : cd.drift_type === 'type_changed' ? 'warning' : 'info'}
                              variant="outlined"
                              sx={{ fontSize: 9, height: 18 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, py: 0.5, fontFamily: 'monospace', color: 'text.secondary' }}>
                            {cd.old_type || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, py: 0.5, fontFamily: 'monospace', color: 'text.secondary' }}>
                            {cd.new_type || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                            {cd.change_class === 'breaking' ? (
                              <Chip label="Breaking" size="small" color="error" sx={{ fontSize: 9, height: 18 }} />
                            ) : cd.change_class === 'compatible' ? (
                              <Chip label="Compatible" size="small" color="success" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {onReplayStep && selectedNode && (
              <Button
                variant="contained"
                size="small"
                onClick={() => onReplayStep(
                  // For target_tables backed by an iterative schema_map op,
                  // re-execution targets the underlying op (where the SQL
                  // and recipe live), not the synthetic target_table node.
                  ((selectedNode as any)._schemaMapOperationId as string | undefined) || selectedNode.stepId,
                )}
                startIcon={<FixIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                {(selectedNode.stepType === 'schema_map'
                  || (selectedNode.stepType === 'target_table' && (selectedNode as any)._schemaMapOperationId))
                  ? 'Re-materialize Mapping'
                  : 'Re-execute Step'}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NodeInspectorPanel;
